import { GridApiPro } from '@mui/x-data-grid-pro'
import { RefObject, useEffect } from 'react'
import type { DragEvent as ReactDragEvent } from 'react'

/** Horizontal edge zone that triggers scroll while reordering columns (px). */
const EDGE_PX = 96
/** Minimum scroll speed at the inner edge of the zone (px / frame @ ~60fps). */
const MIN_SPEED = 4
/** Maximum scroll speed at the outer edge of the zone (px / frame). */
const MAX_SPEED = 16

/**
 * Faster / smoother horizontal auto-scroll while dragging column headers.
 * Replaces the built-in GridScrollArea (20px, event-driven) with a continuous
 * rAF loop and a wider edge zone. Hide `.MuiDataGrid-scrollArea` in grid sx.
 */
export const useInventoryV3ColumnDragScroll = (apiRef: RefObject<GridApiPro | null>) => {
  useEffect(() => {
    const api = apiRef.current
    if (!api?.subscribeEvent) return

    let dragging = false
    let pointerX = 0
    let rafId = 0

    const stopLoop = () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
        rafId = 0
      }
    }

    const tick = () => {
      if (!dragging) {
        rafId = 0
        return
      }

      const root = api.rootElementRef?.current
      const dimensions = api.state?.dimensions
      if (!root || !dimensions) {
        rafId = requestAnimationFrame(tick)
        return
      }

      const rect = root.getBoundingClientRect()
      const leftBound = rect.left + (dimensions.leftPinnedWidth ?? 0)
      const rightBound =
        rect.right - (dimensions.rightPinnedWidth ?? 0) - (dimensions.hasScrollX ? dimensions.scrollbarSize ?? 0 : 0)

      let delta = 0
      if (pointerX < leftBound + EDGE_PX) {
        const depth = 1 - Math.max(0, Math.min(1, (pointerX - leftBound) / EDGE_PX))
        delta = -(MIN_SPEED + depth * (MAX_SPEED - MIN_SPEED))
      } else if (pointerX > rightBound - EDGE_PX) {
        const depth = 1 - Math.max(0, Math.min(1, (rightBound - pointerX) / EDGE_PX))
        delta = MIN_SPEED + depth * (MAX_SPEED - MIN_SPEED)
      }

      if (delta !== 0) {
        const { left, top } = api.getScrollPosition()
        const maxLeft = Math.max(0, (dimensions.columnsTotalWidth ?? 0) - (dimensions.viewportInnerSize?.width ?? 0))
        const nextLeft = Math.max(0, Math.min(maxLeft, left + delta))
        if (nextLeft !== left) {
          api.scroll({ left: nextLeft, top })
        }
      }

      rafId = requestAnimationFrame(tick)
    }

    const onDragStart = (_params: unknown, event: ReactDragEvent<HTMLElement>) => {
      dragging = true
      pointerX = event.clientX
      stopLoop()
      rafId = requestAnimationFrame(tick)
    }

    const onDragEnd = () => {
      dragging = false
      stopLoop()
    }

    const onDragOver = (event: DragEvent) => {
      if (!dragging) return
      pointerX = event.clientX
    }

    const unsubscribeStart = api.subscribeEvent('columnHeaderDragStart', onDragStart)
    const unsubscribeEnd = api.subscribeEvent('columnHeaderDragEnd', onDragEnd)
    document.addEventListener('dragover', onDragOver)

    return () => {
      dragging = false
      stopLoop()
      unsubscribeStart()
      unsubscribeEnd()
      document.removeEventListener('dragover', onDragOver)
    }
  }, [apiRef])
}
